import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CargomaticModal from 'components/v2/modal';
import AddButton from 'components/v2/buttons/AddButton';
import LaneRatesModalContent from 'components/modules/pricing/LaneRatesModalContent';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import Alert from 'components/ui/Alert';
import moment from 'moment-timezone';
import _ from 'lodash';
import AlreadyConfirmedButton from '../../pricing/AlreadyConfirmedButton';

const errorMessage = 'REQUIRED_FIELDS_MISSING_ERROR';
export default function UpdateLaneRatesModal({
  modalType,
  itemMetaData,
  showModal,
  onSave,
  onClickAlreadyConfirmed,
  onComplete,
  onClose,
  onAddRate,
  onFieldChange,
  onChangeDateField,
  onZoneBlur,
  onChangeAddress,
  onChangeCustomer,
  onClickApplyAll,
  onClickRemoveLane,
  missingRequiredFields,
  loadingConfirmation,
  uploadingFiles,
  disableButtons,
}) {
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [error, setError] = useState(null);
  const [dateValidationErrorLength, setDateValidationErrorLength] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    getOverlappingDates();
  }, [itemMetaData]);

  useEffect(() => {
    setSaveDisabled(disableButtons);
  }, [disableButtons]);

  useEffect(() => {
    setDateValidationError();
  }, [dateValidationErrorLength]);

  const handleToggleCheckAll = (checked) => {
    // setItems((prev) => {
    //   return prev.map((item) => ({ ...item, checked }));
    // });
    setCheckAll(checked);
  };
  const renderError = () => {
    if (!error) {
      return null;
    }
    return <Alert type="error">{error}</Alert>;
  };
  const setDateValidationError = () => {
    if (dateValidationErrorLength > 0) {
      setError(t('OVERLAPPING_DATES_ERROR'));
    } else {
      setError(null);
    }
  };

  const getOverlappingDates = () => {
    const rowsGroupedByCustomer = _.groupBy(
      itemMetaData,
      (rate) => rate.customer,
    );
    const rowsGroupedByLane = Object.keys(rowsGroupedByCustomer).reduce(
      (data, customerId) => {
        const laneRates = rowsGroupedByCustomer[customerId];
        data[customerId] = _.groupBy(
          laneRates,
          (laneRate) => `${laneRate.originZip}|${laneRate.destinationZip}`,
        );
        return data;
      },
      {},
    );
    const duplicateRows = Object.keys(rowsGroupedByLane).reduce(
      (data, customerId) => {
        const customerRateDataByLanes = rowsGroupedByLane[customerId];

        // Loop over each data lane we have in rows array
        const duplicates = [];
        Object.keys(customerRateDataByLanes).forEach((lane) => {
          const currentLaneRows = customerRateDataByLanes[lane];
          currentLaneRows.forEach((laneRow, index) => {
            if (
              !_.isEmpty(laneRow.createdAt) &&
              !_.isEmpty(laneRow.updatedAt)
            ) {
              for (let j = index + 1; j < currentLaneRows.length; j++) {
                const createdMoment = moment(laneRow.createdAt).isBetween(
                  currentLaneRows[j].createdAt,
                  currentLaneRows[j].updatedAt,
                  undefined,
                  '[]',
                );
                const updatedMoment = moment(laneRow.updatedAt).isBetween(
                  currentLaneRows[j].createdAt,
                  currentLaneRows[j].updatedAt,
                  undefined,
                  '[]',
                );
                if (createdMoment || updatedMoment) {
                  duplicates.push(currentLaneRows[j]);
                }
              }
            }
          });
        });
        data = data.concat(duplicates);
        return data;
      },
      [],
    );
    if (dateValidationErrorLength !== duplicateRows.length) {
      setDateValidationErrorLength(duplicateRows.length);
    }
  };

  const renderErrorMessage = () => {
    if (!missingRequiredFields) {
      return null;
    }
    return (
      <div className="m-5">
        <ErrorLabel>{t(errorMessage)}</ErrorLabel>
      </div>
    );
  };

  const getTitle = () => {
    return (
      <div>
        <div className="absolute w-1/6 left-3 top-3"> {t('ADD_RATE')} </div>
        <h6 className="mt-5 text-sm text-grey-dark">
          {t('REQUIRED_FIELD_INDICATOR')}
        </h6>
        <AddButton
          className="absolute right-10 top-1"
          onClick={() => onAddRate(modalType)}
        />
      </div>
    );
  };

  const getBody = (items) => {
    const pStyle = {
      fontSize: '0.75rem',
    };
    return (
      <div className="overflow-auto p-1 pb-44">
        {error && renderError()}
        <table className="default-table">
          <thead className="whitespace-nowrap">
            <tr>
              <th>*{t('CARRIER_SHIPPER').toUpperCase()}</th>
              <th>*{t('ORIGIN_ZONE').toUpperCase()}</th>
              <th>{t('ORIGIN_STATE').toUpperCase()}</th>
              <th>*{t('DESTINATION_ZONE').toUpperCase()}</th>
              <th>{t('DESTINATION_STATE').toUpperCase()}</th>
              <th>*{t('RATE', { count: 1 }).toUpperCase()}</th>
              <th>{t('FSC_VALUE').toUpperCase()}</th>
              <th>*{t('REVERSIBLE').toUpperCase()}</th>
              <th>
                <div>
                  <p style={pStyle}>{t('WEEKLY').toUpperCase()}</p>
                  <p style={pStyle}>{t('COMMITMENT').toUpperCase()}</p>
                </div>
              </th>
              <th>* {t('VALIDITY').toUpperCase()}</th>
              <th>{}</th>
              <th>{}</th>
              <th>{}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              return (
                <LaneRatesModalContent
                  modalType={modalType}
                  index={i}
                  key={i}
                  item={item}
                  onLaneRateFieldChange={onFieldChange(i)}
                  onZoneBlur={(item) => onZoneBlur(item, i)}
                  handleDateField={onChangeDateField(i)}
                  handleAddressUpdate={onChangeAddress(i)}
                  handleCustomerField={onChangeCustomer(i)}
                  handleApplyAllButtonClick={onClickApplyAll(i)}
                  missingRequiredFields={missingRequiredFields}
                  onRemoveLaneFromModal={onClickRemoveLane(i)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const getFooter = () => {
    return (
      <div className="flex flex-row-reverse">
        <div className="mt-3">
          <div className="flex justify-end button-group">
            <button
              className="block mr-2 button button-green"
              disabled={saveDisabled || loadingConfirmation}
              onClick={() => onSave(modalType)}
            >
              {loadingConfirmation ? t('LOADING') : t('REQUEST_CONFIRMATION')}
            </button>
            <div className="block button border">
              <div>
                <AlreadyConfirmedButton
                  modalType={modalType}
                  className="dropdown-filter-wrapper"
                  label={t('ALREADY_CONFIRMED')}
                  alreadyConfirmed={onClickAlreadyConfirmed}
                  onHandleComplete={onComplete}
                  buttonDisable={disableButtons}
                  filesUploading={uploadingFiles}
                />
              </div>
            </div>
          </div>
        </div>
        {renderErrorMessage()}
      </div>
    );
  };
  return (
    <CargomaticModal
      title={getTitle()}
      style={{ top: '15%' }}
      show={showModal}
      onHide={onClose}
      body={getBody(itemMetaData)}
      footer={getFooter()}
      dialogClassName="modal-override-large"
    />
  );
}

UpdateLaneRatesModal.propTypes = {
  modalType: PropTypes.string,
  itemMetaData: PropTypes.array,
  showModal: PropTypes.bool,
  missingRequiredFields: PropTypes.bool.isRequired,
  disableButtons: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClickAlreadyConfirmed: PropTypes.func.isRequired,
  onAddRate: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onChangeDateField: PropTypes.func.isRequired,
  onChangeAddress: PropTypes.func.isRequired,
  onChangeCustomer: PropTypes.func.isRequired,
  onClickApplyAll: PropTypes.func.isRequired,
};
